import React, { useState, useMemo, useCallback } from "react";
import {
    ComposableMap,
    Geographies,
    Geography,
    ZoomableGroup 
} from "react-simple-maps";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next'
import { getColor } from "../utils/mapUtils";
import { CommonAPICall } from "../CommonAPiContext/context";

const geoUrl = "/countries.json";

const GlobeMap = ({ globeCountries }) => {
    const { t } = useTranslation()
    const navigate = useNavigate();
    const {
        vendorDistribution
      } = t('globeMap')
    
    const [isDragging, setIsDragging] = useState(false);
    const [tooltip, setTooltip] = useState(null);
    const [position, setPosition] = useState({ coordinates: [7, 7],zoom: 1.01 });
    const { addFilterData } = CommonAPICall()

    const MIN_ZOOM = 1.01;
    const MAX_ZOOM = 10;
    const DEFAULT_CENTER = [7, 7];
    const isZoomInDisabled = position.zoom >= MAX_ZOOM;
    const isZoomOutDisabled = position.zoom <= MIN_ZOOM;
    
    const { min, max } = useMemo(() => {
        if (!globeCountries || globeCountries.length === 0) {
            return { min: 0, max: 1 };
        }

        const values = globeCountries.map((c) => c.vendors);

        return {
            min: Math.min(...values),
            max: Math.max(...values),
        };
    }, [globeCountries]);

    const normalize = (value) => {
        if (max === min) return 1;
        return (value - min) / (max - min);
    };

   

    const getTooltipStyle = (tooltip) => {
        const offset = 10; 

        let top = tooltip.y - 35; 
        let left = tooltip.x + 10; 

        // Prevent going off top
        if (top < 10) {
            top = tooltip.y + offset;
        }

        // Prevent going off right edge
        if (left > window.innerWidth - 140) {
            left = tooltip.x - 140;
        }

        return { top, left };
    };


    // Zoom handlers
    const handleZoomIn = () => {
        if (position.zoom >= MAX_ZOOM) return;
        setPosition((pos) => ({ ...pos, zoom: pos.zoom * 1.5 }));
    };

    const handleZoomOut = () => {
        setPosition((pos) => {
            
            const newZoom = pos.zoom / 1.5;
    

            if (newZoom <= MIN_ZOOM) {
                return {
                    zoom: MIN_ZOOM,
                    coordinates: DEFAULT_CENTER,
                };
            }
    
            return {
                ...pos,
                zoom: newZoom,
            };
        });
    };

    const handleMoveEnd = (position) => {
        setPosition(position);
    };
    const regionNames = new Intl.DisplayNames(['en'], {
        type: 'region',
    });

    const handleMouseEnter = useCallback((evt, country, countryData) => {
        if (!countryData) return;

        setTooltip({
            x: evt.clientX,
            y: evt.clientY,
            content: (
                <div className="flex flex-col">
                    <span className="font-bold border-b border-gray-600 mb-1 pb-1">{country}</span>
                    <span>PO Count: <strong>{countryData.po}</strong></span>
                    <span>Vendor Count: <strong>{countryData.vendors}</strong></span>
                </div>
            ),
        });
    }, []);

    const handleMouseMove = useCallback((evt, countryData) => {
        if (!countryData) return;

        setTooltip((prev) =>
            prev ? { ...prev, x: evt.clientX, y: evt.clientY } : prev
        );
    }, []);

    const handleMouseLeave = useCallback(() => {
        setTooltip(null);
    }, []);

    const user = JSON.parse(localStorage.getItem('userData'))

    const handleClick = useCallback(
        (country, countryData) => {
          if (!countryData) return;
      
          addFilterData({
            hftUserId: user?.hftOracleId,
            tableName: "NewPoList", // use your actual PO table name
           
            clFilter: [
              {
                column: "FACTORY_COUNTRY",
                values: [countryData.country], // IN, CN, etc.
              },
            ],
          });
      
          setTimeout(() => {
            navigate(`/buyer/po_list`);
          }, 500);
        },
        []
      );
    return (
        <div className="w-[50%] flex justify-center items-center flex-col gap-4 sm:w-[100%]
        shadow-lg rounded-md border border-gray-300 p-3 max-h-[360px] min-h-[360px] relative" >
            <p className="text-sm font-medium">{vendorDistribution}</p>
            {/* Zoom Controls Overlay */}
            <div className="absolute bottom-4 right-4 flex flex-col gap-2 z-10">
                <button
                    onClick={handleZoomIn}  
                    disabled={isZoomInDisabled}
                    className={`w-8 h-8 border rounded shadow flex items-center justify-center font-bold
                        ${isZoomInDisabled 
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed" 
                            : "bg-white hover:bg-gray-100"
                        }`}> + </button>
                <button
                    onClick={handleZoomOut}
                    disabled={isZoomOutDisabled}
        className={`w-8 h-8 border rounded shadow flex items-center justify-center font-bold
            ${isZoomOutDisabled 
                ? "bg-gray-100 text-gray-400 cursor-not-allowed" 
                : "bg-white hover:bg-gray-100"
            }`} > - </button>
            </div>

            <div className="globe-container w-full h-full">
                <div className="globe-inner w-full h-full">
                    {tooltip && (
                        <div
                            className="globe-tooltip"
                            style={getTooltipStyle(tooltip)}
                        >
                            {tooltip.content}
                        </div>
                    )}

                    <div className="globe-map" style={{
                        cursor: isDragging ? "grabbing" : "grab",
                    }}>
                        <ComposableMap
                            projection="geoNaturalEarth1"
                            projectionConfig={{
                                scale: 190,
                            }}
                            style={{ width: "100%", height: "100%" }}
                        >
                            {/* ZoomableGroup handles the panning and zooming logic */}
                            <ZoomableGroup
                                zoom={position.zoom}
                                center={position.coordinates}
                                onMoveStart={() => setIsDragging(true)}
                                onMoveEnd={(pos) => {
                                    setIsDragging(false);
                                    handleMoveEnd(pos);
                                }}
                            >
                                <Geographies geography={geoUrl}>
                                    {({ geographies }) =>
                                        geographies.map((geo) => {
                                            const country = geo.properties.name;
                                            const countryIndex = globeCountries?.findIndex(
                                                (item) => regionNames.of(item.country) === country
                                            );
                                            
                                            const validIndex = countryIndex >= 0 ? countryIndex : null;

                                            const countryData = countryIndex !== -1
                                                ? globeCountries[countryIndex]
                                                : null;

                                            const hasData = !!countryData;

                                            return (
                                                <Geography
                                                    key={geo.rsmKey}
                                                    geography={geo}
                                                    className={
                                                        hasData
                                                            ? "geo-default geo-clickable"
                                                            : "geo-default geo-disabled"
                                                    }
                                                    onMouseEnter={(e) =>
                                                        handleMouseEnter(e, country, countryData)
                                                    }
                                                    onMouseMove={(e) =>
                                                        handleMouseMove(e, countryData)
                                                    }
                                                    onMouseLeave={handleMouseLeave}
                                                    onClick={() =>
                                                        handleClick(country, countryData)
                                                    }
                                                    style={{
                                                        default: {
                                                            fill: hasData ? getColor(validIndex) : "#EEE",
                                                            stroke: "#999",
                                                            strokeWidth: 0.5,
                                                            outline: "none",
                                                        },
                                                        hover: {
                                                            fill: hasData ? getColor(validIndex) : "#EEE",
                                                            stroke: "#000",
                                                            strokeWidth: 1,
                                                            outline: "none",
                                                            cursor: hasData ? "pointer" : "default",
                                                            transition: "all 0.2s ease-in-out",
                                                        },
                                                        pressed: {
                                                            fill: hasData ? "#B71C1C" : "#EEE",
                                                            stroke: "#000",
                                                            strokeWidth: 0.8,
                                                            outline: "none",
                                                        },
                                                    }}
                                                />
                                            );
                                        })
                                    }
                                </Geographies>
                            </ZoomableGroup>
                        </ComposableMap>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GlobeMap;